import { useMutation, useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { bankAccountGetBankAccount } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  DateRangePicker,
  type DateRangeValue,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@column/ui';

import { NotificationList } from '~/components/NotificationList';
import { RenderFields } from '~/components/RenderFields';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { bankAccountDropdownGetQueryKey } from '~/lib/api/bankAccounts/bankAccountDropdownQueryKey';
import { BankAccountSelector } from '~/lib/components/selectors';
import { ReportingRepository } from '~/repositories';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReportingServiceGetCustomizedBankAccountStatementParams } from '~/typings/API';
import { getDateFormat } from '~/util';
import { downloadBlob, getPrettyFilename } from '~/util/downloadFile';

const DEFAULT_EARLIEST_DATE = new Date(2022, 0, 1);

export interface ModalCreateStatementProps {
  isOpen?: boolean;
  onClose?: () => void;
  bankAccount?: BankAccount;
  dates?: string[];
}

interface StatementForm {
  bankAccountId: string;
  dateRange: DateRangeValue;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getFilename = (params: ReportingServiceGetCustomizedBankAccountStatementParams, accountName?: string) =>
  getPrettyFilename({
    fileType: 'Custom Statement',
    fileDescription: [...(accountName ? [accountName] : []), `${params.fromDate} to ${params.toDate}`],
    ext: 'csv',
  });

export const ModalCreateStatement: FC<ModalCreateStatementProps> = ({
  isOpen,
  onClose,
  bankAccount: initialBankAccount,
  dates: initialDates,
}) => {
  const { addDangerNotification, addSuccessNotification, clearNotifications } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);

  const defaultValues = useMemo<StatementForm>(() => {
    const [from, to] = initialDates?.length === 2 ? initialDates.toSorted().map(parseDate) : [];
    return { bankAccountId: initialBankAccount?.id ?? '', dateRange: { from, to } };
  }, [initialBankAccount, initialDates]);

  const formMethods = useForm<StatementForm>({ defaultValues });
  const { formState, handleSubmit, reset, trigger, watch } = formMethods;

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      clearNotifications('popup');
    }
  }, [clearNotifications, defaultValues, isOpen, reset]);

  const bankAccountId = watch('bankAccountId');

  const selectedAccountQuery = useQuery({
    queryKey: bankAccountDropdownGetQueryKey(platformId, isSandbox, bankAccountId || undefined),
    queryFn: async () => {
      const { data } = await bankAccountGetBankAccount({
        path: { bank_account_id: bankAccountId },
        throwOnError: true,
      });
      return data;
    },
    enabled: Boolean(isOpen && bankAccountId && !initialBankAccount),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const accountName =
    (initialBankAccount
      ? initialBankAccount.displayName || initialBankAccount.description
      : selectedAccountQuery.data?.display_name || selectedAccountQuery.data?.description) || undefined;
  const accountCreatedAt = initialBankAccount?.createdAt ?? selectedAccountQuery.data?.created_at;

  const earliestDate = useMemo(() => {
    if (!accountCreatedAt) {
      return DEFAULT_EARLIEST_DATE;
    }
    const createdAt = startOfDay(new Date(accountCreatedAt));
    createdAt.setDate(createdAt.getDate() - 1);
    return createdAt;
  }, [accountCreatedAt]);

  useEffect(() => {
    if (formState.isSubmitted) {
      trigger('dateRange');
    }
  }, [earliestDate, formState.isSubmitted, trigger]);

  const statementMutation = useMutation({
    mutationFn: (params: ReportingServiceGetCustomizedBankAccountStatementParams) =>
      ReportingRepository.getCustomizedBankAccountStatement(params),
    onSuccess: (data, params) => {
      downloadBlob(data, getFilename(params, accountName));
      addSuccessNotification({ content: 'Statement created' });
      onClose?.();
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to create statement',
        display: 'popup',
      });
    },
  });

  const isPending = statementMutation.isPending;

  const onSubmit = handleSubmit(
    ({ bankAccountId: id, dateRange }) =>
      statementMutation.mutate({
        bankAccountId: id,
        fromDate: getDateFormat(dateRange.from!),
        toDate: getDateFormat(dateRange.to!),
        type: 'csv',
      }),
    handleSubmitErrors
  );

  const today = startOfDay(new Date());

  const validateDateRange = ({ from, to }: DateRangeValue) => {
    if (!from || !to) {
      return 'Select a start and end date';
    }
    if (from < earliestDate) {
      return 'Start date must be on or after the day before the account was opened';
    }
    return from <= to || 'End date must be on or after the start date';
  };

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(open) => !open && !isPending && onClose?.()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-3 sm:max-w-lg"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector<HTMLElement>('form button')?.focus();
        }}
      >
        <DialogHeader className="-mx-6 -mt-2 flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <DialogTitle>Custom CSV Statement</DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={isPending}
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>

        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={onSubmit} className="-mx-6 -mt-6 [--page-margin:24px]">
            {RenderFields<StatementForm>({
              sections: [
                {
                  fields: [
                    {
                      content: (
                        <DialogDescription>
                          Create a statement{accountName ? ` for ${accountName}` : ''} and download it as a .csv file.
                          For a single day, use the same start and end date.
                        </DialogDescription>
                      ),
                    },
                    {
                      id: 'bankAccountId',
                      label: 'Bank Account',
                      oneColumn: true,
                      hide: Boolean(initialBankAccount),
                      rules: { required: 'Select a Bank Account' },
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <BankAccountSelector
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      id: 'dateRange',
                      label: 'Date Range',
                      oneColumn: true,
                      rules: {
                        required: 'Select a start and end date',
                        validate: (value) => validateDateRange(value as DateRangeValue),
                      },
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <DateRangePicker
                          value={value as DateRangeValue}
                          onValueChange={onChange}
                          onBlur={onBlur}
                          disabledDays={[{ before: earliestDate }, { after: today }]}
                          disabled={isPending}
                          aria-invalid={!!error}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
          </form>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" type="button" onClick={onSubmit} disabled={isPending}>
            <Icon.AnimationDownload running={isPending} className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            {isPending ? 'Creating…' : 'Create Statement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
